import { isFulfilled } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';

import createAxiosThunk from 'store/createAxiosThunk';
import * as selectFusion from '../../fusionServices/selectFusion';
import * as selectAxios from './selectAxios';
import * as sessionThunks from '../session/sessionThunks';

/**
 * Formats the data from Fusions Client Config JSON into format that PG uses
 */
function formatClientConfigData(responseData) {
  const result = {};

  result.IS_WHITELABELED = Boolean(responseData.isWhitelabeled);
  result.PROVIDER_LOOKUP = Boolean(responseData.isProviderLookup);
  result.CLOSED_ACCESS = responseData.isClosedAccess;
  result.EMBOLD_CLIENT_SLUG = responseData.emboldClientSlug;
  result.HAS_PORTAL_INTEGRATION = responseData.hasPortalIntegration;
  result.DISCLAIMER_TEXT = responseData.disclaimerText;
  result.SHOW_SHARE = responseData.showShare ?? true;
  result.SHOW_SCHEDULE_BUTTON = responseData.showScheduleButton ?? true;
  result.SHOW_COST = responseData.showCost;
  result.SHOW_OUTCARE = responseData.showLgbtqCompetency;
  result.SHOW_DR_SCORE = responseData.showDrScore;
  // Benefits Change
  result.BENEFITS_CHANGE_DATE_RANGE = responseData.benefitsChangeDateRange;
  result.SHOW_EMBOLD_RECOMMENDED = responseData.showEmboldRecommended ?? false;
  result.HAS_HIPAA = responseData.hasHipaa ?? false;
  result.SHOW_TOTAL_CARE = responseData.showTotalCare ?? false;
  result.HIDE_SUPPORT = responseData.hideSupport ?? false;
  result.NETWORKS =
    responseData.networks &&
    responseData.networks.reduce((acc, val) => {
      acc[val.slug] = {
        text: val.displayName,
        slug: val.slug,
        networkGroupName: val.networkGroupName || null,
        networkGroupSlug: val.networkGroupSlug || null,
        zipcodes: val.zipcodes || [],
        dataSource: val.dataSource,
        sortOrder: val.sortOrder,
        config: val.config,
      };
      return acc;
    }, {});

  result.SEARCH_ERROR_TEXT = responseData.searchErrorText;
  result.NOTIFICATION_BANNER_TEXT = responseData.notificationBannerText;
  result.NOTIFICATION_BANNER_COLOR = responseData.notificationBannerColor;
  result.NOTIFICATION_BANNER_ICON = responseData.notificationBannerIcon;
  result.NOTIFICATION_BANNER_VARIANT = responseData.notificationBannerVariant;
  result.UPDATE_REGION_TEXT = responseData.networkSelectText;
  if (responseData.mapsUrl) {
    result.MAPS_URL = responseData.mapsUrl;
  }

  result.SHOW_HOSPITAL_AFFILIATIONS = responseData.showHospitalAffiliations ?? true;

  if (responseData.welcomeMessage) {
    result.WELCOME_MESSAGE = responseData.welcomeMessage;
  }
  // Benefits Change
  result.BENEFITS_CHANGE_COVERAGE_DECREASING_TEXT =
    responseData.benefitsChangeCoverageDecreasingText || '';
  result.BENEFITS_CHANGE_COVERAGE_DECREASING_TOOLTIP =
    responseData.benefitsChangeCoverageDecreasingTooltip || '';
  result.BENEFITS_CHANGE_COVERAGE_CHANGE_TITLE =
    responseData.benefitsChangeCoverageChangeTitle || '';
  result.BENEFITS_CHANGE_HIGHER_COVERAGE_GAUGE_TEXT =
    responseData.benefitsChangeHigherCoverageGaugeText || '';
  result.BENEFITS_CHANGE_LOWER_COVERAGE_GAUGE_TEXT =
    responseData.benefitsChangeLowerCoverageGaugeText || '';
  result.BENEFITS_CHANGE_HIGHER_BENEFIT_MODAL_TITLE =
    responseData.benefitsChangeHigherBenefitModalTitle || '';
  result.BENEFITS_CHANGE_HIGHER_BENEFIT_MODAL_TEXT =
    responseData.benefitsChangeHigherBenefitModalText || '';
  result.BENEFITS_CHANGE_LOWER_BENEFIT_MODAL_TITLE =
    responseData.benefitsChangeLowerBenefitModalTitle || '';
  result.BENEFITS_CHANGE_LOWER_BENEFIT_MODAL_TEXT =
    responseData.benefitsChangeLowerBenefitModalText || '';
  result.SURGERY_PLUS_PHONE_NUMBER = responseData.surgeryPlusPhoneNumber;

  if (responseData.headerImage) {
    result.HEADER_IMAGE = responseData.headerImage;
  }

  if (responseData.loginImage) {
    result.LOGIN_IMAGE = responseData.loginImage;
  }

  if (responseData.loginImageAltText) result.LOGIN_IMAGE_ALT_TEXT = responseData.loginImageAltText;

  result.REDIRECT_URL = responseData.redirectUrl || '';

  // Featured provider banner config
  result.FEATURED_PROVIDER_BANNER_TEXT = responseData.featuredProviderBannerText || '';
  result.FEATURED_PROVIDER_TOOLTIP_TEXT = responseData.featuredProviderTooltipText || '';
  result.FEATURED_PROVIDER_LINK = responseData.featuredProviderLink || '';
  result.FEATURED_FACILITY_BANNER_TEXT = responseData.featuredFacilityBannerText || '';
  result.FEATURED_FACILITY_TOOLTIP_TEXT = responseData.featuredFacilityTooltipText || '';
  result.FEATURED_PROVIDER_COLOR = responseData.featuredProviderColor || undefined;
  result.FEATURED_PROVIDER_ICON = responseData.featuredProviderIcon || undefined;
  result.APP_NAME = responseData.appName;
  result.HOW_TO_VIDEO_URL = responseData.howToVideoUrl || undefined;

  return result;
}

export const initializeConfig = createAxiosThunk('initializeConfig', async (args, thunkAPI) => {
  const { getState } = thunkAPI;

  const state = getState();
  const configUrl = selectFusion.configUrl(state);
  const initializeAxios = selectAxios.axiosInstance(state);

  const response = await initializeAxios(configUrl);
  const responseData = response.data;

  return formatClientConfigData(responseData);
});

export const initialize = createAxiosThunk('initialize', async (args, thunkAPI) => {
  const { dispatch } = thunkAPI;

  const configPromise = dispatch(initializeConfig());
  const sessionDataPromise = dispatch(sessionThunks.initializeSession(args));

  const configData = await configPromise;
  const sessionData = await sessionDataPromise;

  const isClosedAccess = configData.payload.CLOSED_ACCESS;
  const noIntegrationToken = !args?.integrationToken;
  const successfulSessionFetch = isFulfilled(sessionThunks.initializeSession)(sessionData);

  if (isClosedAccess && noIntegrationToken && successfulSessionFetch) {
    Sentry.captureException(
      new Error('Closed access client initialized with no integration token')
    );
  }
  return { configData: configData.payload, sessionData: sessionData.payload };
});
